import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import useApi from '../../../services/useApi';
import { IdentificationDetailsState, nationalIdState, paperTazkeraTypes, BankDetail, BusinessDetails, BusinessDetailsTypes, PhotoUpload } from './Components/types';
import { act } from 'react';
import BankDetails from './Components/BankDetails';
import { createAction } from '@reduxjs/toolkit';
import { NOTIFICATION_LANGUAGE_KEY_TO_DISPLAY } from '../../../components/utils/notificationLanguage';
export { NOTIFICATION_LANGUAGE_DISPLAY_TO_KEY, NOTIFICATION_LANGUAGE_KEY_TO_DISPLAY } from '../../../components/utils/notificationLanguage';
import { Nullable } from 'vitest';

// Service Fee Types
interface ServiceFeePostpaidDetail {
  mno_id: string;
  mno_name: string;
  bundle_dispersive_fee_percent: number;
  etopup_dispersive_fee_percent: number;
  status: boolean;
  child_bundle_dispersive_fee_percent?: number;
  child_etopup_dispersive_fee_percent?: number;
}
interface CVMTarget {
  target_amount: number | string;
  on_target_achievement_percentage: number | string;
}

interface CVMDetails {
  from_date: string;
  to_date: string;
  targets: CVMTarget[];
}

interface ServiceFeeUpfront {
  service_fee_type: 'Upfront';
  service_fee_parent: string;
  service_fee_parent_name: string;
  service_fee_parent_id?: string;
  upfront_service_fee_percentage: number;
  child_upfront_service_fee_percentage?: number;
  adjust_child_service_fee?: number;
}

interface ServiceFeePostpaid {
  service_fee_type: 'Postpaid';
  service_fee_parent: string;
  service_fee_parent_name: string;
  service_fee_parent_id?: string;
  service_fee_details: ServiceFeePostpaidDetail[];
  child_service_fee_details?: ServiceFeePostpaidDetail[];
  adjust_child_service_fee?: number;
}

type ServiceFee = ServiceFeeUpfront | ServiceFeePostpaid;

interface AddMDDetails {
  first_name: string;
  last_name: string;
  email_address: string;
  phone_number: string;
  house_no: string;
  date_of_birth: string;
  secondary_phone_numbers?: { country_code: string; phone_number: string }[] | null;
  profession: string;
  market_block_no: string;
  floor_no: string;
  street_name: string;
  city_town: string;
  country: string;
  province: string;
  region: string;
  district: string;
  postal_code: string;
  nationality: string;
  gender: string;
  notification_language: string;
  email_address_verify: string;
  phone_number_verify: string;
  country_code: string;
  latitude: number;
  longitude: number;
  location_name: string;
  photo: PhotoUpload;
  bank_details?: BankDetail[];
  general?: string;
  wallet_access?: string;
  recovery_email_address?: string;
  recovery_email_address_verify?: string;
}

interface AddMDLoader {
  email_address: boolean;
  recovery_email_address: boolean;
  phone_number: boolean;
}

interface AddMDState {
  addAdminDetails: AddMDDetails;
  add_loading: boolean;
  fileError: string | null;
  felidError: string[] | null;
  errors: Partial<Record<keyof AddMDDetails, string>>;
  addMDLoader: AddMDLoader;
  disableAll: boolean;
  wallet_access: string[];
  wallet_access_edit: string[];
  is_api_user: boolean;
  is_api_user_edit: boolean;
  enable_sms: boolean;
  enable_sms_edit: boolean;
  whitelisted_ips: string[];
  whitelisted_ips_edit: string[];
  editLoading: boolean;
  profile: string | null;
  to_date: string | null;
  editData: AddMDDetails;
  isVerifiedFirst: boolean;
  identificationDetails: IdentificationDetails;
  photoUpload: PhotoUpload;
  bankDetails: BankDetail[]
  bankDetailsErrors: BankDetail[]
  businessDetails: BusinessDetailsTypes;
  user_id: string;
  savedData: { [key: string]: any };
  payloadForNationality: {};
  verifiedPhoneNumber: '';
  verifiedEmailAddress: string;
  verifiedRecoveryEmailAddress: string;
  service_fee: ServiceFee;
  serviceFeeErrors: { [key: string]: string };
  mnoState: MNOState;
  cvm: CVMDetails;
  cvmErrors: { [key: string]: string };
  secondary_phone_numbers: { country_code: string; phone_number: string }[] | null;
  has_children: boolean;
}

interface ApiResponse {
  error: boolean;
  data: {
    basic_details: {
      first_name: string;
      last_name: string;
      date_of_birth: string;
      email_address: string;
      phone_number: string;
      profession: string;
      house_no: string;
      market_block_no: string;
      floor_no: string;
      street_name: string;
      city_town: string;
      country: string;
      province: string;
      region: string;
      district: string;
      postal_code: string;
      nationality: string;
      gender: string;
      notification_language: string;
      wallet_access: string[];
      country_code: string;
      latitude: number;
      longitude: number;
      is_api_user?: boolean;
      enable_sms?: boolean;
      ips?: string[];
      whitelisted_ips?: string[];
    },
    identification_details: {
      identification_type: string;
      document_details: {}
    };
    photoUpload: PhotoUpload;
    bankDetails: BankDetail[];
    businessDetails: BusinessDetailsTypes;
    service_fee?: ServiceFee;
    cvm?: CVMDetails;
  } & Partial<AddMDDetails>;
}

interface ErrorPayload {
  message: string;
}

const inputs: AddMDDetails = {
  first_name: '',
  last_name: '',
  date_of_birth: '',
  email_address: '',
  phone_number: '',
  profession: '',
  house_no: '',
  market_block_no: '',
  secondary_phone_numbers: [
    {
      country_code: '',
      phone_number: ''
    },
  ],
  floor_no: '',
  street_name: '',
  city_town: '',
  country: '',
  province: '',
  region: '',
  district: '',
  postal_code: '',
  nationality: '',
  gender: '',
  notification_language: 'English',
  email_address_verify: '',
  phone_number_verify: '',
  country_code: '',
  latitude: 0,
  longitude: 0,
  location_name: '',
  recovery_email_address: '',
  recovery_email_address_verify: ''
};

const loaders: AddMDLoader = {
  email_address: false,
  recovery_email_address: false,
  phone_number: false,
};
interface IdentificationDetails {
  identification_type: string;
  passport?: IdentificationDetailsState;
  national_id?: nationalIdState;
  paper_tazkera?: paperTazkeraTypes;
}
//identification details
const identificationDetails: IdentificationDetails = {
  identification_type: 'Passport',
  passport: {
    passport_number: "",
    passport_valid_from: "",
    passport_valid_to: "",
    visa_number: "",
    visa_category: "",
    visa_issue_date: "",
    visa_expiry_date: "",
    issuer_country: "",
    visa_duration: "",
    passport_front_page: "",
    visa_front_page: ""
  },
  national_id: {
    national_id_number: "",
    national_id_valid_from: "",
    national_id_valid_to: "",
    national_id_front_page: "",
    national_id_back_page: "",
    mothers_name: ""
  },
  paper_tazkera: {
    national_id_number: "",
    paper_tazkera_page_1: "",
    paper_tazkera_page_2: "",
    registration_page_number: "",
    registration_book_number: "",
    registration_row_number: ""
  }
}

// Default service fee state (empty type)
const defaultServiceFee: ServiceFee = {
  service_fee_type: '' as 'Upfront' | 'Postpaid',
  service_fee_parent: '',
  service_fee_parent_name: '',
  service_fee_parent_id: '',
  upfront_service_fee_percentage: 0
};

// MNO Types
interface MNOData {
  id: string;
  name: string;
  contract_start_date: string;
  contract_end_date: string;
  created_at: string;
  updated_at: string;
  etopup_dispersive_fee_percent: string;
  bundle_dispersive_fee_percent: string;
  etopup_balance: string;
  bundle_balance: string;
}

interface MNOState {
  mnoList: MNOData[];
  maxUpfrontServiceFeePercent: number;
  selectedMNOs: { [key: string]: { etopupPercent: number; bundlePercent: number } };
  loading: boolean;
  error: string | null;
}

const initialState: AddMDState = {
  addAdminDetails: { ...inputs },
  add_loading: false,
  fileError: null,
  felidError: null,
  errors: {},
  addMDLoader: { ...loaders },
  disableAll: false,
  wallet_access: [],
  is_api_user: false,
  is_api_user_edit: false,
  enable_sms: true,
  enable_sms_edit: true,
  whitelisted_ips: [''],
  whitelisted_ips_edit: [],
  editLoading: false,
  editData: { ...inputs },
  isVerifiedFirst: false,
  profile: '',
  to_date: '',
  wallet_access_edit: [],
  identificationDetails: { ...identificationDetails },
  bankDetails: [{
    bank_name: '',
    account_number: '',
    bank_passbook_front_page: '',
  }],
  bankDetailsErrors: [{
    bank_name: '',
    account_number: '',
    bank_passbook_front_page: '',
  }],
  photoUpload: '',
  businessDetails: {
    store_name: '',
    business_type: '',
    business_license_number: '',
    business_valid_from: '',
    business_valid_to: '',
    business_license_front_page: '',
    business_license_back_page: '',
  },
  user_id: '', // 93bdc346-add2-4c21-b868-f60c3565a6ad
  savedData: {
    // ee: {}, e: {}, eee: {}, ww: {}, tt: {}, yy: {}
  },
  payloadForNationality: {},
  verifiedPhoneNumber: '',
  verifiedEmailAddress: '',
  verifiedRecoveryEmailAddress: '',
  service_fee: defaultServiceFee,
  serviceFeeErrors: {},
  mnoState: {
    mnoList: [],
    maxUpfrontServiceFeePercent: 0,
    selectedMNOs: {},
    loading: false,
    error: null
  },
  cvm: {
    from_date: '',
    to_date: '',
    targets: [{
      target_amount: '',
      on_target_achievement_percentage: ''
    }]
  },
  cvmErrors: {},
  secondary_phone_numbers: null,
  has_children: false,
};

export const fetchMDView = createAsyncThunk<ApiResponse, string, { rejectValue: ErrorPayload }>(
  'addMd/fetchMDView',
  async (payload, { rejectWithValue }) => {
    try {
      const api = useApi();
      const ApiResponse = await api.getAPI(`${payload}`);
      return ApiResponse.data;
    } catch (error: any) {
      console.error('Error fetching list:', error);
      return rejectWithValue({ message: error.message || 'Unknown error occurred' });
    }
  }
);

const addAdminSlice = createSlice({
  name: 'Add Admin',
  initialState,
  reducers: {
    resetAddMD: (state) => {
      // Store the current service fee state
      const currentServiceFee = state.service_fee;
      const currentServiceFeeErrors = state.serviceFeeErrors;
      const currentMNOState = state.mnoState;

      // Reset to initial state
      Object.assign(state, initialState);

      // Restore the service fee state
      state.service_fee = currentServiceFee;
      state.serviceFeeErrors = currentServiceFeeErrors;
      state.mnoState = currentMNOState;
    },
    setUpdateMDStates: (
      state,
      action: PayloadAction<{ key: string; value: string | number }>
    ) => {
      const { key, value } = action.payload;
      if (key in state.addAdminDetails) {
        state.addAdminDetails = {
          ...state.addAdminDetails,
          [key]: value
        };
        state.errors = {
          ...state.errors,
          [key]: ''
        };
      }
    },
    setAddMDLoader: (
      state,
      action: PayloadAction<{ key: string; value: boolean }>
    ) => {
      const { key, value } = action.payload;
      if (key in state.addMDLoader) {
        state.addMDLoader = {
          ...state.addMDLoader,
          [key]: value
        };
        state.disableAll = value;
      }
    },
    setDisableState: (state, action: PayloadAction<boolean>) => {
      state.disableAll = action.payload;
    },
    setIsVerifiedFirst: (state, action: PayloadAction<boolean>) => {
      state.isVerifiedFirst = action.payload;
    },
    setAddLoading: (state, action: PayloadAction<boolean>) => {
      state.add_loading = action.payload;
    },
    setFileError: (state, action: PayloadAction<string | null>) => {
      state.fileError = action.payload;
    },
    setAddMDError: (state, action: PayloadAction<Partial<AddMDDetails>>) => {
      state.errors = { ...state.errors, ...action.payload };
    },
    setCheckBox: (state, action: PayloadAction<string>) => {
      const element = action.payload;

      if (element === 'eTop Up' || element === 'Digital wallet') {
        state.wallet_access = state.wallet_access.includes(element)
          ? state.wallet_access.filter(item => item !== element)
          : [...state.wallet_access, element];
      }
    },
    setIsApiUser: (state, action: PayloadAction<boolean>) => {
      state.is_api_user = action.payload;
      if (!action.payload) {
        state.whitelisted_ips = [''];
      }
    },
    setEnableSms: (state, action: PayloadAction<boolean>) => {
      state.enable_sms = action.payload;
    },
    setWhitelistedIps: (state, action: PayloadAction<string[]>) => {
      state.whitelisted_ips = action.payload;
    },
    setAddMDFelidError: (state, action: PayloadAction<string | null>) => {
      if (action.payload !== null) {
        state.felidError = state.felidError
          ? [...state.felidError, action.payload]
          : [action.payload];
      }
    },
    setUserId: (state, action: PayloadAction<string | null>) => {
      state.user_id = action.payload || '';
    },
    setProfileStatus: (state, action: PayloadAction<string | null>) => {
      state.profile = action.payload || '';
    },
    setToDate: (state, action: PayloadAction<string | null>) => {
      state.to_date = action.payload || '';
    },
    setIdentificationDetails: (state, action: PayloadAction<{ id: keyof IdentificationDetails; subId: string; value: string }>) => {
      const { id, subId, value } = action.payload;

      if (id === 'identification_type') {
        state.identificationDetails.identification_type = value;
      } else {
        state.identificationDetails = {
          ...state.identificationDetails,
          [id]: {
            ...(state.identificationDetails[id] as Record<string, string>),
            [subId]: value
          }
        };
      }
    },
    setParticularDetails: (state, action: PayloadAction<{ id: string; value: any }>) => {
      const { id, value } = action.payload;
      state.identificationDetails = initialState.identificationDetails;
      const key = id.toLowerCase().replace(/ /g, '_') as keyof IdentificationDetails;
      state.identificationDetails = {
        ...state.identificationDetails, // Keep existing properties
        [key]: value, // Update the specific property
      };
      state.identificationDetails.identification_type = id;
    },
    resetIdentificationDetails: (state, action: PayloadAction<{ id: keyof IdentificationDetails }>) => {
      const { id } = action.payload;
      state.identificationDetails[id] = initialState.identificationDetails[id];
    },
    setSavedData: (state, action: PayloadAction<{ key: string; value: object | string }>) => {
      const { key, value } = action.payload;
      state.savedData[key] = value;
    },
    setEditSavedData: (state, action) => {
      state.savedData = action.payload
    },
    setBankDetails: (state, action: PayloadAction<{ key: string; value: string; index: number }>) => {
      const { key, value, index } = action.payload;
      state.bankDetails[index] = {
        ...state.bankDetails[index],
        [key]: value,
      };
      state.bankDetailsErrors[index] = {
        ...state.bankDetailsErrors[index],
        [key]: ''
      };
    },
    setEditBankDetails: (state, action) => {
      if (action.payload.length > 0) {
        state.bankDetails = action.payload;
        state.bankDetailsErrors = action.payload.map(() => ({
          bank_name: '',
          account_number: '',
          bank_passbook_front_page: '',
        }));
      } else {
        state.bankDetails = initialState.bankDetails;
        state.bankDetailsErrors = initialState.bankDetailsErrors;
      }
    },
    setBankDetailsAdd: (state) => {
      state.bankDetails.push({
        bank_name: '',
        account_number: '',
        bank_passbook_front_page: '',
      });
      state.bankDetailsErrors.push({
        bank_name: '',
        account_number: '',
        bank_passbook_front_page: '',
      });
    },
    setBankDetailsRemove: (state, action: PayloadAction<number>) => {
      state.bankDetails.splice(action.payload, 1);
      state.bankDetailsErrors.splice(action.payload, 1);
    },
    setBankErrors: (state, action) => {
      state.bankDetailsErrors = action.payload || [];
    },
    resetPhotoUploads: (state) => {
      state.photoUpload = initialState.photoUpload;
    },
    resetBankDetails: (state) => {
      state.bankDetails = initialState.bankDetails;
    },
    setBusinessDetails: (state, action: PayloadAction<{ key: string; value: string }>) => {
      const { key, value } = action.payload;
      state.businessDetails = {
        ...state.businessDetails,
        [key]: value
      };
    },
    setEditBusinessDetails: (state, action) => {
      const data = action.payload;
      if (data && Object.keys(data).length > 0) {
        state.businessDetails = { ...state.businessDetails, ...data };
      } else {
        state.businessDetails = initialState.businessDetails;
      }
    },
    setPayloadFornationality: (state, action) => {
      state.payloadForNationality = action.payload;
    },
    setSecondaryPhoneNumbers: (state, action: PayloadAction<{ country_code: string; phone_number: string }[] | null>) => {
      state.secondary_phone_numbers = action.payload;
    },
    updateMDDetails: (state, action: PayloadAction<ApiResponse>) => {
      const { data } = action.payload;
      if (!action.payload.error) {
        const dataObj: Partial<AddMDDetails> = {
          first_name: data?.basic_details?.first_name || '',
          last_name: data?.basic_details?.last_name || '',
          date_of_birth: data?.basic_details?.date_of_birth || '',
          email_address: data?.basic_details?.email_address || '',
          phone_number: `${data?.basic_details?.country_code?.split('+')[1] || ''}${data?.basic_details?.phone_number || ''}`,
          profession: data?.basic_details?.profession || '',
          house_no: data?.basic_details?.house_no || '',
          market_block_no: data?.basic_details?.market_block_no || '',
          floor_no: data?.basic_details?.floor_no || '',
          street_name: data?.basic_details?.street_name || '',
          city_town: data?.basic_details?.city_town || '',
          country: data?.basic_details?.country || '',
          province: data?.basic_details?.province || '',
          region: data?.basic_details?.region || '',
          district: data?.basic_details?.district || '',
          postal_code: data?.basic_details?.postal_code || '',
          nationality: data?.basic_details?.nationality || '',
          gender: data?.basic_details?.gender || '',
          notification_language: NOTIFICATION_LANGUAGE_KEY_TO_DISPLAY[data?.basic_details?.notification_language] || 'English',
          email_address_verify: data?.basic_details?.email_address ? 'true' : 'false',
          phone_number_verify: data?.basic_details?.phone_number ? 'true' : 'false',
          country_code: data?.basic_details?.country_code?.split('+')[1] || '',
          latitude: data?.basic_details?.latitude || 0,
          longitude: data?.basic_details?.longitude || 0,
          recovery_email_address: data?.basic_details?.recovery_email_address || '',
          wallet_access: data?.basic_details?.wallet_access?.includes('etop_up') ? ['eTop Up'] : [],
        }

        // Update the state
        state.addAdminDetails = { ...state.addAdminDetails, ...dataObj };
        state.editData = { ...state.editData, ...dataObj };
        state.editLoading = false;
        state.isVerifiedFirst = true;
        state.wallet_access = data?.basic_details?.wallet_access?.includes('etop_up') ? ['eTop Up'] : [];
        state.wallet_access_edit = data?.basic_details?.wallet_access?.includes('etop_up') ? ['eTop Up'] : [];
        state.is_api_user = data?.basic_details?.is_api_user || false;
        state.is_api_user_edit = data?.basic_details?.is_api_user || false;
        state.enable_sms = data?.basic_details?.enable_sms ?? true;
        state.enable_sms_edit = data?.basic_details?.enable_sms ?? true;
        // Prefer 'whitelisted_ips' (saved payload key) over 'ips' (initial API load / DB column)
        // savedData can have both: 'ips' from initial fetch, 'whitelisted_ips' from subsequent saves
        const ipsData = data?.basic_details?.whitelisted_ips ?? data?.basic_details?.ips;
        state.whitelisted_ips = (ipsData && ipsData.length > 0) ? ipsData : [''];
        state.whitelisted_ips_edit = ipsData ?? [];

        // Clear errors for updated fields
        Object.keys(dataObj).forEach((key) => {
          state.errors[key as keyof AddMDDetails] = '';
        });

        // Set identification details
        if (data.identification_details) {
          state.identificationDetails = {
            identification_type: data?.identification_details?.identification_type || '',
            [data?.identification_details?.identification_type?.toLowerCase().replace(/ /g, '_')]: data?.identification_details?.document_details || '',
          };
        } else {
          state.identificationDetails = initialState.identificationDetails;
        }

        // Set bank details
        if (data.bank_details && data.bank_details.length > 0) {
          state.bankDetails = data.bank_details;
          state.bankDetailsErrors = data.bank_details.map(() => ({
            bank_name: '',
            account_number: '',
            bank_passbook_front_page: '',
          }));
        } else {
          state.bankDetails = initialState.bankDetails;
          state.bankDetailsErrors = initialState.bankDetailsErrors;
        }

        //set Business details
        if (data.business_details && Object.keys(data.business_details).length > 0) {
          state.businessDetails = { ...state.businessDetails, ...data.business_details };
        } else {
          state.businessDetails = initialState.businessDetails;
        }

        // Set service fee if available
        if (data.service_fee) {
          state.service_fee = data.service_fee;
        }
      } else {
        console.error("Error in API response:", action.payload);
      }
    },
    setVerifiedPhoneNumber: (state, action) => {
      state.verifiedPhoneNumber = action.payload || ''
    },
    setVerifiedEmailAddress: (state, action) => {
      state.verifiedEmailAddress = action.payload || ''
    },
    // Service Fee Actions
    setServiceFeeType: (state, action: PayloadAction<'Upfront' | 'Postpaid'>) => {
      const feeType = action.payload;

      if (feeType === 'Upfront') {
        state.service_fee = {
          service_fee_type: 'Upfront',
          service_fee_parent: (state.service_fee as ServiceFeeUpfront).service_fee_parent || '',
          service_fee_parent_name: (state.service_fee as ServiceFeeUpfront).service_fee_parent_name || '',
          service_fee_parent_id: (state.service_fee as ServiceFeeUpfront).service_fee_parent_id || '',
          upfront_service_fee_percentage: (state.service_fee as ServiceFeeUpfront).upfront_service_fee_percentage || 0,
          adjust_child_service_fee: (state.service_fee as ServiceFeeUpfront).adjust_child_service_fee || 0
        };
      } else {
        state.service_fee = {
          service_fee_type: 'Postpaid',
          service_fee_parent: (state.service_fee as ServiceFeePostpaid).service_fee_parent || '',
          service_fee_parent_name: (state.service_fee as ServiceFeePostpaid).service_fee_parent_name || '',
          service_fee_parent_id: (state.service_fee as ServiceFeePostpaid).service_fee_parent_id || '',
          service_fee_details: (state.service_fee as ServiceFeePostpaid).service_fee_details || [],
          child_service_fee_details: (state.service_fee as ServiceFeePostpaid).child_service_fee_details || [],
          adjust_child_service_fee: (state.service_fee as ServiceFeePostpaid).adjust_child_service_fee || 0
        };
      }
    },
    setUpfrontServiceFee: (state, action: PayloadAction<number>) => {
      if (state.service_fee.service_fee_type === 'Upfront') {
        (state.service_fee as ServiceFeeUpfront).upfront_service_fee_percentage = action.payload;
      }
    },
    setPostpaidServiceFeeDetails: (state, action: PayloadAction<ServiceFeePostpaidDetail[]>) => {
      if (state.service_fee.service_fee_type === 'Postpaid') {
        (state.service_fee as ServiceFeePostpaid).service_fee_details = action.payload;
      }
    },
    addPostpaidServiceFeeDetail: (state, action: PayloadAction<ServiceFeePostpaidDetail>) => {
      if (state.service_fee.service_fee_type === 'Postpaid') {
        (state.service_fee as ServiceFeePostpaid).service_fee_details.push(action.payload);
      }
    },
    updatePostpaidServiceFeeDetail: (state, action: PayloadAction<{ index: number, detail: Partial<ServiceFeePostpaidDetail> }>) => {
      const { index, detail } = action.payload;
      if (state.service_fee.service_fee_type === 'Postpaid' &&
        (state.service_fee as ServiceFeePostpaid).service_fee_details[index]) {
        (state.service_fee as ServiceFeePostpaid).service_fee_details[index] = {
          ...(state.service_fee as ServiceFeePostpaid).service_fee_details[index],
          ...detail
        };
      }
    },
    removePostpaidServiceFeeDetail: (state, action: PayloadAction<number>) => {
      if (state.service_fee.service_fee_type === 'Postpaid') {
        (state.service_fee as ServiceFeePostpaid).service_fee_details.splice(action.payload, 1);
      }
    },
    setCompleteServiceFee: (state, action: PayloadAction<ServiceFee>) => {
      state.service_fee = action.payload;
    },
    setServiceFeeErrors: (state, action: PayloadAction<{ [key: string]: string }>) => {
      state.serviceFeeErrors = action.payload;
    },
    clearServiceFeeErrors: (state) => {
      state.serviceFeeErrors = {};
    },
    setServiceFeeParent: (state, action: PayloadAction<{ parent: string, parentId?: string }>) => {
      const { parent, parentId } = action.payload;
      
      if (state.service_fee) {
        state.service_fee.service_fee_parent = parent;
        
        // If it's ZoodPay, we don't need parent ID
        if (parent === 'ZoodPay') {
          state.service_fee.service_fee_parent_id = '';
        } else if (parentId) {
          state.service_fee.service_fee_parent_id = parentId;
        }
      }
    },
    // MNO Actions
    setMNOList: (state, action: PayloadAction<{ data: MNOData[], maxUpfrontServiceFeePercent: number }>) => {
      state.mnoState.mnoList = action.payload.data;
      state.mnoState.maxUpfrontServiceFeePercent = action.payload.maxUpfrontServiceFeePercent;
      state.mnoState.loading = false;
      state.mnoState.error = null;
    },
    setMNOLoading: (state, action: PayloadAction<boolean>) => {
      state.mnoState.loading = action.payload;
    },
    setMNOError: (state, action: PayloadAction<string | null>) => {
      state.mnoState.error = action.payload;
    },
    selectMNO: (state, action: PayloadAction<{ mnoId: string, etopupPercent: number, bundlePercent: number }>) => {
      const { mnoId, etopupPercent, bundlePercent } = action.payload;
      state.mnoState.selectedMNOs[mnoId] = { etopupPercent, bundlePercent };
    },
    removeMNO: (state, action: PayloadAction<string>) => {
      const mnoId = action.payload;
      delete state.mnoState.selectedMNOs[mnoId];
    },
    clearSelectedMNOs: (state) => {
      state.mnoState.selectedMNOs = {};
    },
    updateMNOPercentages: (state, action: PayloadAction<{ mnoId: string, etopupPercent?: number, bundlePercent?: number }>) => {
      const { mnoId, etopupPercent, bundlePercent } = action.payload;
      if (state.mnoState.selectedMNOs[mnoId]) {
        if (etopupPercent !== undefined) {
          state.mnoState.selectedMNOs[mnoId].etopupPercent = etopupPercent;
        }
        if (bundlePercent !== undefined) {
          state.mnoState.selectedMNOs[mnoId].bundlePercent = bundlePercent;
        }
      }
    },
    setCVMDates: (state, action: PayloadAction<{ from_date?: string; to_date?: string }>) => {
      const { from_date, to_date } = action.payload;

      if (from_date !== undefined) {
        state.cvm.from_date = from_date;
      }

      if (to_date !== undefined) {
        state.cvm.to_date = to_date;
      }

      // Clear any errors when dates are updated
      if (from_date) state.cvmErrors['from_date'] = '';
      if (to_date) state.cvmErrors['to_date'] = '';
    },

    addCVMTarget: (state) => {
      state.cvm.targets.push({
        target_amount: '',
        on_target_achievement_percentage: ''
      });
    },

    updateCVMTarget: (state, action: PayloadAction<{
      index: number;
      field: keyof CVMTarget;
      value: number | string
    }>) => {
      const { index, field, value } = action.payload;

      if (index >= 0 && index < state.cvm.targets.length) {
        state.cvm.targets[index][field] = value;

        // Clear errors for this target when updated
        state.cvmErrors[`targets[${index}].${field}`] = '';
      }
    },

    removeCVMTarget: (state, action: PayloadAction<number>) => {
      const index = action.payload;

      if (index >= 0 && index < state.cvm.targets.length) {
        state.cvm.targets.splice(index, 1);

        // If we deleted the last target, add an empty one
        if (state.cvm.targets.length === 0) {
          state.cvm.targets.push({
            target_amount: 0,
            on_target_achievement_percentage: 0
          });
        }
      }
    },

    setCVMErrors: (state, action: PayloadAction<{ [key: string]: string }>) => {
      state.cvmErrors = action.payload;
    },

    clearCVMErrors: (state) => {
      state.cvmErrors = {};
    },

    setCompleteCVM: (state, action: PayloadAction<CVMDetails>) => {
      state.cvm = action.payload;
    },

    resetCVM: (state) => {
      state.cvm = initialState.cvm;
      state.cvmErrors = {};
    },
    setPhotoUpload: (state, action: PayloadAction<PhotoUpload>) => {
      state.photoUpload = action.payload;
    },
    setHasChildren: (state, action: PayloadAction<boolean>) => {
      state.has_children = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMDView.pending, (state) => {
        state.disableAll = true;
        state.editLoading = true;
      })
      .addCase(fetchMDView.fulfilled, (state, action: PayloadAction<ApiResponse>) => {
        state.disableAll = false;
        if (!action.payload.error) {
          const { data } = action.payload;
          // Update the state with the data from the API
          const dataObj: Partial<AddMDDetails> = {
            first_name: data?.basic_details?.first_name || '',
            last_name: data.basic_details.last_name || '',
            date_of_birth: data.basic_details.date_of_birth || '',
            email_address: data.basic_details.email_address || '',
            phone_number: `${data?.basic_details.country_code ? data.basic_details.country_code.split('+')[1] : ''}${data.basic_details.phone_number ? ' ' + data.basic_details.phone_number : ''}`,
            profession: data.basic_details.profession || '',
            house_no: data.basic_details.house_no || '',
            market_block_no: data.basic_details.market_block_no || '',
            floor_no: data.basic_details.floor_no || '',
            street_name: data.basic_details.street_name || '',
            city_town: data.basic_details.city_town || '',
            country: data.basic_details.country || '',
            province: data.basic_details.province || '',
            region: data.basic_details.region || '',
            district: data.basic_details.district || '',
            postal_code: data.basic_details.postal_code || '',
            nationality: data.basic_details.nationality || '',
            gender: data.basic_details.gender || '',
            notification_language: NOTIFICATION_LANGUAGE_KEY_TO_DISPLAY[data.basic_details.notification_language] || 'English',
            email_address_verify: data.basic_details.email_address ? 'true' : 'false',
            phone_number_verify: data.basic_details.phone_number ? 'true' : 'false',
            country_code: data.basic_details.country_code ? data.basic_details.country_code.split('+')[1] : '',
          };

          // Update the state with the new data
          state.addAdminDetails = {
            ...state.addAdminDetails,
            ...dataObj,
          };
          state.editData = {
            ...state.editData,
            ...dataObj,
          };
          state.editLoading = false;
          state.isVerifiedFirst = true;
          state.wallet_access = data.basic_details.wallet_access?.includes('etop_up') ? ['eTop Up'] : [];
          state.wallet_access_edit = data.basic_details.wallet_access?.includes('etop_up') ? ['eTop Up'] : [];
          state.is_api_user = data.basic_details.is_api_user || false;
          state.is_api_user_edit = data.basic_details.is_api_user || false;
          state.enable_sms = data.basic_details.enable_sms ?? true;
          state.enable_sms_edit = data.basic_details.enable_sms ?? true;
          state.whitelisted_ips = (data.basic_details.ips && data.basic_details.ips.length > 0) ? data.basic_details.ips : [''];
          state.whitelisted_ips_edit = data.basic_details.ips ?? [];

          // Clear any errors for the fields that were updated
          Object.keys(dataObj).forEach((key) => {
            state.errors[key as keyof AddMDDetails] = '';
          });

          // Example: Set identification details if available in the API response
          if (data['identification_details']) {
            let identification = {
              identification_type: data['identification_details'].identification_type || '',
              [data['identification_details'].identification_type.toLowerCase().replace(/ /g, '_')]: data['identification_details'].document_details || '',
            }
            state.identificationDetails = { ...state.identificationDetails, ...identification }
          } else {
            state.identificationDetails = initialState.identificationDetails;
          }

          // Example: Set bank details if available in the API response
          if (data.bankDetails) {
            state.bankDetails = data.bankDetails;
          }

          // Example: Set business details if available in the API response
          if (data.businessDetails) {
            state.businessDetails = data.businessDetails;
          }

          // Set service fee if available
          if (data.service_fee) {
            state.service_fee = data.service_fee;
          }

          if (data.cvm) {
            state.cvm = data.cvm;
          }

        } else {
          console.error('Error in API response:', action.payload);
        }
      }).addCase(fetchMDView.rejected, (state, action: PayloadAction<ErrorPayload | undefined>) => {
        state.editLoading = false;
        console.error('Error fetching details:', action.payload?.message);
      });
  }
});

export const {
  setIsVerifiedFirst,
  resetAddMD,
  setUpdateMDStates,
  setAddLoading,
  setAddMDLoader,
  setFileError,
  setAddMDError,
  setSecondaryPhoneNumbers,
  setAddMDFelidError,
  setCheckBox,
  setUserId,
  setProfileStatus,
  setToDate,
  setIdentificationDetails,
  setBankDetails,
  setEditBankDetails,
  setBankDetailsAdd,
  setBankDetailsRemove,
  setBankErrors,
  resetIdentificationDetails,
  setParticularDetails,
  setBusinessDetails,
  setEditBusinessDetails,
  setSavedData,
  setEditSavedData,
  resetPhotoUploads,
  resetBankDetails,
  updateMDDetails,
  setPayloadFornationality,
  setDisableState,
  setVerifiedPhoneNumber,
  setVerifiedEmailAddress,
  // Service Fee Actions
  setServiceFeeType,
  setUpfrontServiceFee,
  setPostpaidServiceFeeDetails,
  addPostpaidServiceFeeDetail,
  updatePostpaidServiceFeeDetail,
  removePostpaidServiceFeeDetail,
  setCompleteServiceFee,
  setServiceFeeErrors,
  clearServiceFeeErrors,
  setServiceFeeParent,
  // MNO Actions
  setMNOList,
  setMNOLoading,
  setMNOError,
  selectMNO,
  removeMNO,
  clearSelectedMNOs,
  updateMNOPercentages,
  // CVM
  setCVMDates,
  addCVMTarget,
  updateCVMTarget,
  removeCVMTarget,
  setCVMErrors,
  clearCVMErrors,
  setCompleteCVM,
  resetCVM,
  setPhotoUpload,
  setHasChildren,
  setIsApiUser,
  setEnableSms,
  setWhitelistedIps,
} = addAdminSlice.actions;

export const setChildUpfrontServiceFee = createAction<number>('addMD/setChildUpfrontServiceFee');
export const setChildPostpaidServiceFeeDetails = createAction<ServiceFeePostpaidDetail[]>('addMD/setChildPostpaidServiceFeeDetails');
export const setVerifiedRecoveryEmailAddress = createAction<string>('addMD/setVerifiedRecoveryEmailAddress');

export default addAdminSlice.reducer;